import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { categoryService } from '../../../services/categoryService';

const CategoriesSection = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingCategory, setEditingCategory] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addingSubcategoryTo, setAddingSubcategoryTo] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [expandedCategories, setExpandedCategories] = useState(new Set());

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const data = await categoryService?.getHierarchical();
      setCategories(data || []);
    } catch (err) {
      console.error('Error loading categories:', err);
      setError('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (categoryId, hasSubcategories) => {
    const message = hasSubcategories 
      ? 'This category has subcategories. Deleting it will also delete all subcategories. Are you sure?'
      : 'Are you sure you want to delete this category? This action cannot be undone.';
    
    if (!window.confirm(message)) {
      return;
    }

    try {
      await categoryService?.delete(categoryId);
      setSuccess('Category deleted successfully');
      loadCategories();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error deleting category:', err);
      setError(err?.message || 'Failed to delete category');
      setTimeout(() => setError(''), 3000);
    }
  };

  const toggleExpanded = (categoryId) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded?.has(categoryId)) {
      newExpanded?.delete(categoryId);
    } else {
      newExpanded?.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const typeOptions = [
    { value: 'all', label: 'All Types' },
    { value: 'income', label: 'Income' },
    { value: 'expense', label: 'Expense' },
    { value: 'payment', label: 'Payment' },
    { value: 'transfer', label: 'Transfer' },
    { value: 'transfer_in', label: 'Transfer In' },
    { value: 'transfer_out', label: 'Transfer Out' }
  ];

  const filteredCategories = filterType === 'all' 
    ? categories 
    : categories?.filter(cat => cat?.type === filterType);

  const getTypeColor = (type) => {
    const colors = {
      income: 'text-success',
      expense: 'text-destructive',
      payment: 'text-blue-500',
      transfer: 'text-primary',
      transfer_in: 'text-purple-500',
      transfer_out: 'text-pink-500'
    };
    return colors?.[type] || 'text-muted-foreground';
  };

  return (
    <div className="bg-card rounded-lg shadow-elevation-2 p-12 md:p-20 lg:p-24">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-12 mb-16 md:mb-20">
        <div className="flex items-center gap-8 md:gap-12">
          <Icon name="Tag" size={20} className="md:w-24 md:h-24" color="var(--color-primary)" />
          <h2 className="text-lg md:text-xl font-heading font-semibold text-card-foreground">Categories</h2>
        </div>
        <Button
          variant="default"
          size="sm"
          iconName="Plus"
          iconPosition="left"
          onClick={() => setShowAddModal(true)}
          className="w-full sm:w-auto"
        >
          Add Category
        </Button>
      </div>
      <p className="text-xs md:text-sm text-muted-foreground mb-16 md:mb-24">
        Manage your transaction categories and subcategories. Preset categories are provided by default but can be edited or deleted.
      </p>
      {success && (
        <div className="mb-20 p-16 bg-success/10 border border-success/20 rounded-lg flex items-start gap-12">
          <Icon name="CheckCircle" size={20} color="var(--color-success)" />
          <p className="text-sm text-success flex-1">{success}</p>
        </div>
      )}
      {error && (
        <div className="mb-20 p-16 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-12">
          <Icon name="AlertCircle" size={20} color="var(--color-destructive)" />
          <p className="text-sm text-destructive flex-1">{error}</p>
        </div>
      )}
      <div className="mb-20">
        <Select
          label="Filter by Type"
          options={typeOptions}
          value={filterType}
          onChange={(value) => setFilterType(value)}
        />
      </div>
      {loading ? (
        <div className="text-center py-24 md:py-32">
          <div className="animate-spin rounded-full h-10 w-10 md:h-12 md:w-12 border-b-2 border-primary mx-auto mb-12 md:mb-16"></div>
          <p className="text-muted-foreground font-caption text-sm">Loading categories...</p>
        </div>
      ) : filteredCategories?.length === 0 ? (
        <div className="text-center py-24 md:py-32">
          <Icon name="Tag" size={40} className="md:w-48 md:h-48" color="var(--color-muted-foreground)" className="mx-auto mb-12 md:mb-16 opacity-50" />
          <p className="text-muted-foreground font-caption text-sm">No categories found</p>
        </div>
      ) : (
        <div className="space-y-8 md:space-y-12">
          {filteredCategories?.map((category) => (
            <div key={category?.id} className="border border-border rounded-lg overflow-hidden">
              {/* Parent Category */}
              <div className="flex items-center justify-between p-12 md:p-16 bg-muted/30 hover:bg-muted/50 transition-smooth gap-8">
                <div className="flex items-center gap-8 md:gap-16 flex-1 min-w-0">
                  {category?.subcategories?.length > 0 && (
                    <button
                      onClick={() => toggleExpanded(category?.id)}
                      className="text-muted-foreground hover:text-foreground transition-smooth flex-shrink-0"
                    >
                      <Icon 
                        name={expandedCategories?.has(category?.id) ? "ChevronDown" : "ChevronRight"} 
                        size={18}
                        className="md:w-20 md:h-20"
                      />
                    </button>
                  )}
                  <div
                    className="w-32 h-32 md:w-40 md:h-40 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: category?.color + '20' }}
                  >
                    {category?.emoji ? (
                      <span className="text-base md:text-xl">{category?.emoji}</span>
                    ) : (
                      <Icon
                        name={category?.icon || 'Tag'}
                        size={16}
                        className="md:w-20 md:h-20"
                        color={category?.color}
                      />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-6 md:gap-12 mb-2 md:mb-4 flex-wrap">
                      <h3 className="text-xs md:text-sm font-caption font-semibold text-foreground truncate">
                        {category?.name}
                      </h3>
                      {category?.isPreset && (
                        <span className="px-6 py-1 md:px-8 md:py-2 bg-primary/10 text-primary text-[10px] md:text-xs font-caption rounded flex-shrink-0">
                          Preset
                        </span>
                      )}
                      {category?.subcategories?.length > 0 && (
                        <span className="px-6 py-1 md:px-8 md:py-2 bg-muted text-muted-foreground text-[10px] md:text-xs font-caption rounded flex-shrink-0">
                          {category?.subcategories?.length} sub
                        </span>
                      )}
                    </div>
                    <span className={`text-[10px] md:text-xs font-caption capitalize ${getTypeColor(category?.type)}`}>
                      {category?.type?.replace('_', ' ')}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-4 md:gap-8 flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Plus"
                    onClick={() => setAddingSubcategoryTo(category)}
                    title="Add Subcategory"
                    className="h-32 w-32 md:h-auto md:w-auto p-4 md:p-8"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Edit"
                    onClick={() => setEditingCategory(category)}
                    className="h-32 w-32 md:h-auto md:w-auto p-4 md:p-8"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Trash2"
                    onClick={() => handleDelete(category?.id, category?.subcategories?.length > 0)}
                    className="h-32 w-32 md:h-auto md:w-auto p-4 md:p-8"
                  />
                </div>
              </div>

              {/* Subcategories */}
              {expandedCategories?.has(category?.id) && category?.subcategories?.length > 0 && (
                <div className="bg-card">
                  {category?.subcategories?.map((subcategory) => (
                    <div
                      key={subcategory?.id}
                      className="flex items-center justify-between p-12 md:p-16 pl-40 md:pl-72 border-t border-border hover:bg-muted/30 transition-smooth gap-8"
                    >
                      <div className="flex items-center gap-8 md:gap-16 flex-1 min-w-0">
                        <div
                          className="w-24 h-24 md:w-32 md:h-32 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: subcategory?.color + '20' }}
                        >
                          <Icon
                            name={subcategory?.icon || 'Tag'}
                            size={12}
                            className="md:w-16 md:h-16"
                            color={subcategory?.color}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-xs md:text-sm font-caption text-foreground truncate">
                            {subcategory?.name}
                          </h4>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 md:gap-8 flex-shrink-0">
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="Edit"
                          onClick={() => setEditingCategory(subcategory)}
                          className="h-32 w-32 md:h-auto md:w-auto p-4 md:p-8"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="Trash2"
                          onClick={() => handleDelete(subcategory?.id, false)}
                          className="h-32 w-32 md:h-auto md:w-auto p-4 md:p-8"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add Category Modal */}
      {showAddModal && (
        <AddCategoryModal
          onSubmit={async (categoryData) => {
            try {
              await categoryService?.create(categoryData);
              setSuccess('Category added successfully');
              loadCategories();
              setShowAddModal(false);
              setTimeout(() => setSuccess(''), 3000);
            } catch (err) {
              console.error('Error adding category:', err);
              setError(err?.message || 'Failed to add category');
              setTimeout(() => setError(''), 3000);
            }
          }}
          onClose={() => setShowAddModal(false)}
        />
      )}

      {/* Add Subcategory Modal */}
      {addingSubcategoryTo && (
        <AddSubcategoryModal
          parentCategory={addingSubcategoryTo}
          onSubmit={async (subcategoryData) => {
            try {
              await categoryService?.create({
                ...subcategoryData,
                parentId: addingSubcategoryTo?.id,
                type: addingSubcategoryTo?.type
              });
              setSuccess('Subcategory added successfully');
              loadCategories();
              setAddingSubcategoryTo(null);
              setTimeout(() => setSuccess(''), 3000);
            } catch (err) {
              console.error('Error adding subcategory:', err);
              setError(err?.message || 'Failed to add subcategory');
              setTimeout(() => setError(''), 3000);
            }
          }}
          onClose={() => setAddingSubcategoryTo(null)}
        />
      )}

      {/* Edit Category Modal */}
      {editingCategory && (
        <EditCategoryModal
          category={editingCategory}
          onSubmit={async (categoryData) => {
            try {
              await categoryService?.update(editingCategory?.id, categoryData);
              setSuccess('Category updated successfully');
              loadCategories();
              setEditingCategory(null);
              setTimeout(() => setSuccess(''), 3000);
            } catch (err) {
              console.error('Error updating category:', err);
              setError(err?.message || 'Failed to update category');
              setTimeout(() => setError(''), 3000);
            }
          }}
          onClose={() => setEditingCategory(null)}
        />
      )}
    </div>
  );
};

// Add Category Modal Component
const AddCategoryModal = ({ onSubmit, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    type: 'expense',
    color: '#6366f1',
    icon: 'Tag',
    emoji: ''
  });

  const handleSubmit = (e) => {
    e?.preventDefault();
    onSubmit(formData);
  };

  const typeOptions = [
    { value: 'income', label: 'Income' },
    { value: 'expense', label: 'Expense' },
    { value: 'payment', label: 'Payment' },
    { value: 'transfer', label: 'Transfer' },
    { value: 'transfer_in', label: 'Transfer In' },
    { value: 'transfer_out', label: 'Transfer Out' }
  ];

  // Common icon options for categories
  const iconOptions = [
    { value: 'Tag', label: 'Tag' },
    { value: 'ShoppingCart', label: 'Shopping Cart' },
    { value: 'Home', label: 'Home' },
    { value: 'Car', label: 'Car' },
    { value: 'Utensils', label: 'Utensils' },
    { value: 'Coffee', label: 'Coffee' },
    { value: 'Heart', label: 'Heart' },
    { value: 'Zap', label: 'Zap' },
    { value: 'Smartphone', label: 'Smartphone' },
    { value: 'Wifi', label: 'Wifi' },
    { value: 'Tv', label: 'TV' },
    { value: 'Music', label: 'Music' },
    { value: 'Film', label: 'Film' },
    { value: 'Book', label: 'Book' },
    { value: 'Briefcase', label: 'Briefcase' },
    { value: 'DollarSign', label: 'Dollar Sign' },
    { value: 'CreditCard', label: 'Credit Card' },
    { value: 'Gift', label: 'Gift' },
    { value: 'Plane', label: 'Plane' },
    { value: 'MapPin', label: 'Map Pin' },
    { value: 'Activity', label: 'Activity' },
    { value: 'TrendingUp', label: 'Trending Up' },
    { value: 'PiggyBank', label: 'Piggy Bank' },
    { value: 'Wallet', label: 'Wallet' },
    { value: 'Building', label: 'Building' },
    { value: 'Users', label: 'Users' },
    { value: 'Star', label: 'Star' },
    { value: 'Award', label: 'Award' },
    { value: 'Target', label: 'Target' },
    { value: 'ArrowUpRight', label: 'Arrow Up Right' },
    { value: 'ArrowDownLeft', label: 'Arrow Down Left' },
    { value: 'ArrowLeftRight', label: 'Arrow Left Right' },
    { value: 'MoreHorizontal', label: 'More Horizontal' },
    { value: 'AlertCircle', label: 'Alert Circle' }
  ];

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-background/80 backdrop-blur-sm">
      <div className="bg-card rounded-lg shadow-elevation-4 w-full max-w-[500px] mx-16 max-h-[90vh] overflow-y-auto">
        <div className="p-20 md:p-24 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-12">
            <Icon name="Plus" size={24} color="var(--color-primary)" />
            <h2 className="text-xl font-heading font-semibold text-card-foreground">
              Add Category
            </h2>
          </div>
          <Button variant="ghost" size="icon" iconName="X" onClick={onClose} />
        </div>

        <form onSubmit={handleSubmit} className="p-20 md:p-24">
          <div className="space-y-16">
            <div>
              <label className="block text-sm font-caption font-medium text-foreground mb-8">
                Category Name *
              </label>
              <Input
                type="text"
                value={formData?.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e?.target?.value }))}
                placeholder="e.g., Entertainment"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-caption font-medium text-foreground mb-8">
                Type *
              </label>
              <Select
                options={typeOptions}
                value={formData?.type}
                onChange={(value) => setFormData(prev => ({ ...prev, type: value }))}
              />
            </div>

            <div>
              <label className="block text-sm font-caption font-medium text-foreground mb-8">
                Emoji (Optional)
              </label>
              <Input
                type="text"
                value={formData?.emoji}
                onChange={(e) => setFormData(prev => ({ ...prev, emoji: e?.target?.value }))}
                placeholder="e.g., 🎮"
                maxLength={2}
              />
              <p className="text-xs text-muted-foreground mt-4">
                Add an emoji to display instead of an icon
              </p>
            </div>

            <div>
              <label className="block text-sm font-caption font-medium text-foreground mb-8">
                Icon
              </label>
              <Select
                options={iconOptions}
                value={formData?.icon}
                onChange={(value) => setFormData(prev => ({ ...prev, icon: value }))}
              />
              <p className="text-xs text-muted-foreground mt-4">
                Icon is used when no emoji is provided
              </p>
            </div>

            <div>
              <label className="block text-sm font-caption font-medium text-foreground mb-8">
                Color
              </label>
              <Input
                type="color"
                value={formData?.color}
                onChange={(e) => setFormData(prev => ({ ...prev, color: e?.target?.value }))}
              />
            </div>

            {/* Preview */}
            <div className="p-16 bg-muted/30 rounded-lg">
              <p className="text-xs font-caption font-medium text-muted-foreground mb-8">Preview</p>
              <div className="flex items-center gap-12">
                <div
                  className="w-40 h-40 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: formData?.color + '20' }}
                >
                  {formData?.emoji ? (
                    <span className="text-xl">{formData?.emoji}</span>
                  ) : (
                    <Icon
                      name={formData?.icon}
                      size={20}
                      color={formData?.color}
                    />
                  )}
                </div>
                <span className="text-sm font-medium text-foreground">{formData?.name || 'Category Name'}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-12 mt-24">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" variant="default" className="flex-1">
              Add Category
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Add Subcategory Modal Component
const AddSubcategoryModal = ({ parentCategory, onSubmit, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    color: parentCategory?.color || '#6366f1',
    icon: 'Tag'
  });

  const handleSubmit = (e) => {
    e?.preventDefault();
    onSubmit(formData);
  };

  // Common icon options for subcategories
  const iconOptions = [
    { value: 'Tag', label: 'Tag' },
    { value: 'ShoppingCart', label: 'Shopping Cart' },
    { value: 'Home', label: 'Home' },
    { value: 'Car', label: 'Car' },
    { value: 'Utensils', label: 'Utensils' },
    { value: 'Coffee', label: 'Coffee' },
    { value: 'Heart', label: 'Heart' },
    { value: 'Zap', label: 'Zap' },
    { value: 'Smartphone', label: 'Smartphone' },
    { value: 'Wifi', label: 'Wifi' },
    { value: 'Tv', label: 'TV' },
    { value: 'Music', label: 'Music' },
    { value: 'Film', label: 'Film' },
    { value: 'Book', label: 'Book' },
    { value: 'Briefcase', label: 'Briefcase' },
    { value: 'DollarSign', label: 'Dollar Sign' },
    { value: 'CreditCard', label: 'Credit Card' },
    { value: 'Gift', label: 'Gift' },
    { value: 'Plane', label: 'Plane' },
    { value: 'MapPin', label: 'Map Pin' },
    { value: 'Activity', label: 'Activity' },
    { value: 'TrendingUp', label: 'Trending Up' },
    { value: 'PiggyBank', label: 'Piggy Bank' },
    { value: 'Wallet', label: 'Wallet' },
    { value: 'Building', label: 'Building' },
    { value: 'Users', label: 'Users' },
    { value: 'Star', label: 'Star' },
    { value: 'Award', label: 'Award' },
    { value: 'Target', label: 'Target' },
    { value: 'ArrowUpRight', label: 'Arrow Up Right' },
    { value: 'ArrowDownLeft', label: 'Arrow Down Left' },
    { value: 'ArrowLeftRight', label: 'Arrow Left Right' },
    { value: 'MoreHorizontal', label: 'More Horizontal' },
    { value: 'AlertCircle', label: 'Alert Circle' }
  ];

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-background/80 backdrop-blur-sm">
      <div className="bg-card rounded-lg shadow-elevation-4 w-full max-w-[500px] mx-16 max-h-[90vh] overflow-y-auto">
        <div className="p-20 md:p-24 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-12">
            <Icon name="Plus" size={24} color="var(--color-primary)" />
            <h2 className="text-xl font-heading font-semibold text-card-foreground">
              Add Subcategory to {parentCategory?.name}
            </h2>
          </div>
          <Button variant="ghost" size="icon" iconName="X" onClick={onClose} />
        </div>

        <form onSubmit={handleSubmit} className="p-20 md:p-24">
          <div className="space-y-16">
            <div>
              <label className="block text-sm font-caption font-medium text-foreground mb-8">
                Subcategory Name *
              </label>
              <Input
                type="text"
                value={formData?.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e?.target?.value }))}
                placeholder="e.g., Netflix"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-caption font-medium text-foreground mb-8">
                Icon
              </label>
              <Select
                options={iconOptions}
                value={formData?.icon}
                onChange={(value) => setFormData(prev => ({ ...prev, icon: value }))}
              />
            </div>

            <div>
              <label className="block text-sm font-caption font-medium text-foreground mb-8">
                Color
              </label>
              <Input
                type="color"
                value={formData?.color}
                onChange={(e) => setFormData(prev => ({ ...prev, color: e?.target?.value }))}
              />
            </div>

            {/* Preview */}
            <div className="p-16 bg-muted/30 rounded-lg">
              <p className="text-xs font-caption font-medium text-muted-foreground mb-8">Preview</p>
              <div className="flex items-center gap-12">
                <div
                  className="w-32 h-32 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: formData?.color + '20' }}
                >
                  <Icon
                    name={formData?.icon}
                    size={16}
                    color={formData?.color}
                  />
                </div>
                <span className="text-sm font-medium text-foreground">{formData?.name || 'Subcategory Name'}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-12 mt-24">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" variant="default" className="flex-1">
              Add Subcategory
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Category Modal Component
const EditCategoryModal = ({ category, onSubmit, onClose }) => {
  const [formData, setFormData] = useState({
    name: category?.name || '',
    color: category?.color || '#6366f1',
    emoji: category?.emoji || '',
    icon: category?.icon || 'Tag'
  });

  const handleSubmit = (e) => {
    e?.preventDefault();
    onSubmit(formData);
  };

  // Common icon options
  const iconOptions = [
    { value: 'Tag', label: 'Tag' },
    { value: 'ShoppingCart', label: 'Shopping Cart' },
    { value: 'Home', label: 'Home' },
    { value: 'Car', label: 'Car' },
    { value: 'Utensils', label: 'Utensils' },
    { value: 'Coffee', label: 'Coffee' },
    { value: 'Heart', label: 'Heart' },
    { value: 'Zap', label: 'Zap' },
    { value: 'Smartphone', label: 'Smartphone' },
    { value: 'Wifi', label: 'Wifi' },
    { value: 'Tv', label: 'TV' },
    { value: 'Music', label: 'Music' },
    { value: 'Film', label: 'Film' },
    { value: 'Book', label: 'Book' },
    { value: 'Briefcase', label: 'Briefcase' },
    { value: 'DollarSign', label: 'Dollar Sign' },
    { value: 'CreditCard', label: 'Credit Card' },
    { value: 'Gift', label: 'Gift' },
    { value: 'Plane', label: 'Plane' },
    { value: 'MapPin', label: 'Map Pin' },
    { value: 'Activity', label: 'Activity' },
    { value: 'TrendingUp', label: 'Trending Up' },
    { value: 'PiggyBank', label: 'Piggy Bank' },
    { value: 'Wallet', label: 'Wallet' },
    { value: 'Building', label: 'Building' },
    { value: 'Users', label: 'Users' },
    { value: 'Star', label: 'Star' },
    { value: 'Award', label: 'Award' },
    { value: 'Target', label: 'Target' },
    { value: 'ArrowUpRight', label: 'Arrow Up Right' },
    { value: 'ArrowDownLeft', label: 'Arrow Down Left' },
    { value: 'ArrowLeftRight', label: 'Arrow Left Right' },
    { value: 'MoreHorizontal', label: 'More Horizontal' },
    { value: 'AlertCircle', label: 'Alert Circle' }
  ];

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-background/80 backdrop-blur-sm">
      <div className="bg-card rounded-lg shadow-elevation-4 w-full max-w-[500px] mx-16 max-h-[90vh] overflow-y-auto">
        <div className="p-20 md:p-24 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-12">
            <Icon name="Edit" size={24} color="var(--color-primary)" />
            <h2 className="text-xl font-heading font-semibold text-card-foreground">
              Edit {category?.parentId ? 'Subcategory' : 'Category'}
            </h2>
          </div>
          <Button variant="ghost" size="icon" iconName="X" onClick={onClose} />
        </div>

        <form onSubmit={handleSubmit} className="p-20 md:p-24">
          <div className="space-y-16">
            <div>
              <label className="block text-sm font-caption font-medium text-foreground mb-8">
                Name *
              </label>
              <Input
                type="text"
                value={formData?.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e?.target?.value }))}
                required
              />
            </div>

            {!category?.parentId && (
              <div>
                <label className="block text-sm font-caption font-medium text-foreground mb-8">
                  Emoji (Optional)
                </label>
                <Input
                  type="text"
                  value={formData?.emoji}
                  onChange={(e) => setFormData(prev => ({ ...prev, emoji: e?.target?.value }))}
                  placeholder="e.g., 🎮"
                  maxLength={2}
                />
                <p className="text-xs text-muted-foreground mt-4">
                  Add an emoji to display instead of an icon
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-caption font-medium text-foreground mb-8">
                Icon
              </label>
              <Select
                options={iconOptions}
                value={formData?.icon}
                onChange={(value) => setFormData(prev => ({ ...prev, icon: value }))}
              />
              <p className="text-xs text-muted-foreground mt-4">
                {category?.parentId ? 'Icon for subcategory' : 'Icon is used when no emoji is provided'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-caption font-medium text-foreground mb-8">
                Color
              </label>
              <Input
                type="color"
                value={formData?.color}
                onChange={(e) => setFormData(prev => ({ ...prev, color: e?.target?.value }))}
              />
            </div>

            {/* Preview */}
            <div className="p-16 bg-muted/30 rounded-lg">
              <p className="text-xs font-caption font-medium text-muted-foreground mb-8">Preview</p>
              <div className="flex items-center gap-12">
                <div
                  className={`${category?.parentId ? 'w-32 h-32' : 'w-40 h-40'} rounded-lg flex items-center justify-center`}
                  style={{ backgroundColor: formData?.color + '20' }}
                >
                  {formData?.emoji && !category?.parentId ? (
                    <span className="text-xl">{formData?.emoji}</span>
                  ) : (
                    <Icon
                      name={formData?.icon}
                      size={category?.parentId ? 16 : 20}
                      color={formData?.color}
                    />
                  )}
                </div>
                <span className="text-sm font-medium text-foreground">{formData?.name || (category?.parentId ? 'Subcategory Name' : 'Category Name')}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-12 mt-24">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" variant="default" className="flex-1">
              Update {category?.parentId ? 'Subcategory' : 'Category'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CategoriesSection;